import { useState } from "react";
import { useParams } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import {
  useWorkspace,
  useInviteMember,
  useRemoveMember,
  useUpdateMemberRole,
  type InviteMemberInput,
} from "../../hooks/useWorkspace";
import { useMe } from "../../hooks/useMe";

const ROLE_OPTIONS: ReadonlyArray<{
  value: InviteMemberInput["role"];
  label: string;
}> = [
  { value: "operator", label: "Operator" },
  { value: "approver", label: "Approver" },
  { value: "reviewer", label: "Reviewer" },
  { value: "agent_handler", label: "Agent handler" },
  { value: "cxo", label: "CXO" },
];

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((p) => p.charAt(0).toUpperCase())
    .filter(Boolean)
    .slice(0, 2)
    .join("");
}

function MembersInner() {
  const { workspaceId = "" } = useParams<{ workspaceId: string }>();
  const ws = useWorkspace(workspaceId);
  const me = useMe();
  const invite = useInviteMember(workspaceId);
  const remove = useRemoveMember(workspaceId);
  const updateRole = useUpdateMemberRole(workspaceId);

  const [email, setEmail] = useState("");
  const [role, setRole] = useState<InviteMemberInput["role"]>("operator");

  const isCxo = ws.data?.currentUserRole === "cxo";
  const memberships = ws.data?.memberships ?? [];
  const accepted = memberships.filter((m) => m.acceptedAt !== null);
  const pending = memberships.filter((m) => m.acceptedAt === null);

  async function handleInvite(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!email) return;
    try {
      await invite.mutateAsync({ email, role });
      setEmail("");
    } catch {
      /* surfaced via mutation state below */
    }
  }

  return (
    <div className="main-inner" data-testid="members">
      <div className="greet-row">
        <span className="greet">Members</span>
        <span className="greet-date">
          {accepted.length} ACTIVE · {pending.length} PENDING
        </span>
      </div>
      <div className="subgreet">
        <div className="section-eyebrow">Workspace roster</div>
        <div className="lede">
          {isCxo
            ? "Invite members and manage roles. Removing the last CXO is blocked."
            : "Read-only — only the CXO can invite or remove members."}
        </div>
      </div>

      {isCxo ? (
        <form
          className="callout"
          onSubmit={handleInvite}
          data-testid="members-invite-form"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 180px auto",
            gap: 8,
            alignItems: "center",
            marginTop: 16,
          }}
        >
          <input
            type="email"
            required
            placeholder="email@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="members-invite-email"
            aria-label="Invite email"
            style={{
              padding: "8px 10px",
              border: "1px solid var(--border)",
              borderRadius: 6,
              fontSize: 13,
            }}
          />
          <select
            value={role}
            onChange={(e) =>
              setRole(e.target.value as InviteMemberInput["role"])
            }
            data-testid="members-invite-role"
            aria-label="Invite role"
            style={{
              padding: "8px 10px",
              border: "1px solid var(--border)",
              borderRadius: 6,
              fontSize: 13,
              background: "white",
            }}
          >
            {ROLE_OPTIONS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
          <button
            type="submit"
            className="btn primary"
            disabled={invite.isPending || !email}
            data-testid="members-invite-submit"
          >
            {invite.isPending ? "Sending…" : "Invite"}
          </button>
          {invite.isError ? (
            <div
              data-testid="members-invite-error"
              style={{
                gridColumn: "1 / -1",
                fontSize: 12,
                color: "var(--warn)",
              }}
            >
              {invite.error instanceof Error
                ? invite.error.message
                : "Invite failed"}
            </div>
          ) : null}
          {invite.isSuccess ? (
            <div
              data-testid="members-invite-success"
              style={{
                gridColumn: "1 / -1",
                fontSize: 12,
                color: "var(--accent)",
              }}
            >
              Invite sent.
            </div>
          ) : null}
        </form>
      ) : null}

      {/* Active members */}
      <div className="section-h">
        <span className="ttl">Active members</span>
        <span className="meta" data-testid="members-active-meta">
          {accepted.length}
        </span>
      </div>
      {updateRole.isError ? (
        <div
          data-testid="member-role-error"
          style={{
            padding: "8px 12px",
            marginBottom: 8,
            fontSize: 12,
            color: "var(--warn)",
            background: "var(--warn-soft, #fbf2e0)",
            border: "1px solid var(--warn)",
            borderRadius: 6,
          }}
        >
          {updateRole.error instanceof Error
            ? `Couldn't change role: ${updateRole.error.message}`
            : "Couldn't change role."}
        </div>
      ) : null}
      <div data-testid="members-active-list">
        {ws.isLoading ? (
          <div
            data-testid="members-skeleton"
            style={{
              padding: 16,
              border: "1px solid var(--border)",
              borderRadius: 8,
              background: "var(--bg-soft)",
              fontSize: 12,
              color: "var(--text-3)",
            }}
          >
            Loading members…
          </div>
        ) : accepted.length === 0 ? (
          <div
            data-testid="members-active-empty"
            style={{
              padding: 16,
              border: "1px dashed var(--border)",
              borderRadius: 8,
              fontSize: 13,
              color: "var(--text-3)",
              textAlign: "center",
            }}
          >
            No active members yet.
          </div>
        ) : (
          accepted.map((m) => (
            <div
              key={m.userId}
              data-testid={`member-${m.userId}`}
              style={{
                display: "grid",
                gridTemplateColumns: "32px 1fr 100px 90px 80px",
                gap: 12,
                alignItems: "center",
                padding: "12px 14px",
                border: "1px solid var(--border)",
                borderRadius: 8,
                marginBottom: 6,
                background: "white",
                fontSize: 13,
              }}
            >
              <div className="avatar" style={{ width: 28, height: 28, fontSize: 11 }}>
                {initials(m.displayName || m.email)}
              </div>
              <div>
                <div style={{ fontWeight: 600 }}>
                  {m.displayName || m.email}
                </div>
                <div style={{ fontSize: 11, color: "var(--text-3)" }}>
                  {m.email}
                </div>
              </div>
              {isCxo && m.userId !== me.data?.user.id ? (
                <select
                  data-testid={`member-${m.userId}-role-select`}
                  aria-label={`Role for ${m.displayName || m.email}`}
                  value={m.role}
                  disabled={
                    updateRole.isPending &&
                    updateRole.variables?.userId === m.userId
                  }
                  onChange={(e) => {
                    const newRole = e.target.value as InviteMemberInput["role"];
                    if (newRole === m.role) return;
                    updateRole.mutate({ userId: m.userId, role: newRole });
                  }}
                  style={{
                    justifySelf: "start",
                    padding: "3px 6px",
                    border: "1px solid var(--border)",
                    borderRadius: 6,
                    fontSize: 12,
                    background: "white",
                    cursor: "pointer",
                  }}
                >
                  {ROLE_OPTIONS.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
              ) : (
                <span
                  className="pill"
                  data-testid={`member-${m.userId}-role`}
                  style={{ justifySelf: "start" }}
                  title={
                    m.userId === me.data?.user.id
                      ? "You can't change your own role"
                      : undefined
                  }
                >
                  {m.role}
                </span>
              )}
              <span
                style={{ fontSize: 11, color: "var(--text-3)" }}
                title={m.acceptedAt ?? ""}
              >
                {m.acceptedAt
                  ? new Date(m.acceptedAt).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                    })
                  : "—"}
              </span>
              <div style={{ textAlign: "right" }}>
                {isCxo ? (
                  <button
                    type="button"
                    className="btn"
                    data-testid={`member-${m.userId}-remove`}
                    onClick={() => {
                      if (
                        window.confirm(
                          `Remove ${m.displayName || m.email}? This is irreversible.`,
                        )
                      ) {
                        remove.mutate(m.userId);
                      }
                    }}
                    disabled={remove.isPending}
                    style={{ fontSize: 11, padding: "3px 8px" }}
                    aria-label={`Remove ${m.displayName || m.email}`}
                  >
                    Remove
                  </button>
                ) : null}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pending invites — derived from memberships with acceptedAt = null */}
      <div className="section-h">
        <span className="ttl">Pending invites</span>
        <span className="meta" data-testid="members-pending-meta">
          {pending.length}
        </span>
      </div>
      <div data-testid="members-pending-list">
        {pending.length === 0 ? (
          <div
            data-testid="members-pending-empty"
            style={{
              padding: 16,
              border: "1px dashed var(--border)",
              borderRadius: 8,
              fontSize: 13,
              color: "var(--text-3)",
              textAlign: "center",
            }}
          >
            No pending invites.
          </div>
        ) : (
          pending.map((m) => (
            <div
              key={m.userId}
              data-testid={`pending-${m.userId}`}
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 100px 100px",
                gap: 12,
                alignItems: "center",
                padding: "12px 14px",
                border: "1px solid var(--border)",
                borderRadius: 8,
                marginBottom: 6,
                background: "white",
                fontSize: 13,
              }}
            >
              <div>{m.email}</div>
              <span className="pill amber">{m.role}</span>
              <span style={{ fontSize: 11, color: "var(--text-3)" }}>
                invited{" "}
                {m.invitedAt
                  ? new Date(m.invitedAt).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                    })
                  : "—"}
              </span>
            </div>
          ))
        )}
      </div>

    </div>
  );
}

function MembersErrorFallback({
  error,
  resetErrorBoundary,
}: {
  error: Error;
  resetErrorBoundary: () => void;
}) {
  return (
    <div className="main-inner" data-testid="members-error">
      <div className="greet-row">
        <span className="greet">Members unavailable</span>
      </div>
      <div className="subgreet">
        <div className="section-eyebrow">ERROR</div>
        <div className="lede">{error.message}</div>
      </div>
      <div style={{ marginTop: 16 }}>
        <button
          type="button"
          className="btn primary"
          onClick={resetErrorBoundary}
        >
          Reload
        </button>
      </div>
    </div>
  );
}

export function Members() {
  return (
    <ErrorBoundary FallbackComponent={MembersErrorFallback}>
      <MembersInner />
    </ErrorBoundary>
  );
}
